/**
 * Task-less pod identity detection (Claudespace v3 project runners AND adhoc
 * scratch pods).
 *
 * A v3 task-less pod's bootstrap bundle carries a session JWT with
 * {projectId, workspaceId, sessionId, role} and NO taskId claim; the entrypoint
 * exports CONVEYOR_PROJECT_ID / CONVEYOR_SESSION_ID / CONVEYOR_WORKSPACE_ID from
 * the claims, does NOT export CONVEYOR_TASK_ID, and sets CONVEYOR_MODE to "pm"
 * (project runner) or "adhoc" (interactive scratch TUI). Both share this
 * identity shape — cli.ts branches on CONVEYOR_MODE afterward to pick the
 * runner. Every condition below is required — anything else falls through to
 * the task-runner path, which fail-closed exits without a task id.
 *
 * `workspaceId` is the stable `Workspace` row id — unlike `sessionId` (the
 * `WorkspaceSession` row), it survives a sleep/wake cycle: a user-initiated
 * wake mints a brand-new writer `WorkspaceSession` (fresh-session semantics,
 * see `ensure-adhoc.ts`), so `sessionId` changes on every wake. The adhoc
 * runner keys its Claude session lineage on `workspaceId` for exactly this
 * reason — keying on `sessionId` would never resume the prior transcript.
 */

export interface ProjectRunnerIdentity {
  projectId: string;
  sessionId: string;
  workspaceId: string;
  taskToken: string;
}

export function resolveProjectRunnerIdentity(
  env: Record<string, string | undefined>,
): ProjectRunnerIdentity | null {
  // A task id always wins: this is a task pod, never a project runner.
  if (env.CONVEYOR_TASK_ID) return null;
  if (!env.CONVEYOR_TASK_TOKEN) return null;
  if (!env.CONVEYOR_PROJECT_ID || !env.CONVEYOR_SESSION_ID || !env.CONVEYOR_WORKSPACE_ID) {
    return null;
  }
  // Task-less identity is shared by the project runner ("pm") and the adhoc
  // scratch-pod TUI ("adhoc"); cli.ts branches on CONVEYOR_MODE afterward to
  // pick the runner. Any OTHER mode (reader/review/task/absent) is not a valid
  // task-less identity. NOTE: the adhoc branch in cli.ts gates on THIS resolver
  // returning non-null AND CONVEYOR_MODE === "adhoc" — omitting "adhoc" here
  // makes that branch unreachable and crash-loops every adhoc pod at the
  // task-mode fall-through.
  if (env.CONVEYOR_MODE !== "pm" && env.CONVEYOR_MODE !== "adhoc") return null;

  return {
    projectId: env.CONVEYOR_PROJECT_ID,
    sessionId: env.CONVEYOR_SESSION_ID,
    workspaceId: env.CONVEYOR_WORKSPACE_ID,
    taskToken: env.CONVEYOR_TASK_TOKEN,
  };
}
